import { useState } from "react";
import { cn } from "@/lib/utils";

// Deterministic avatar tint so each person keeps the same color across the
// directory, profile header, and any other surface that shows their initials.
const avatarTones = [
  "bg-info-bg text-info-foreground",
  "bg-success-bg text-success-foreground",
  "bg-warning-bg text-warning-foreground",
  "bg-accent text-primary",
  "bg-danger-bg text-danger-foreground",
] as const;

const sizeStyles = {
  sm: "size-7 rounded-md text-3xs",
  md: "size-9 rounded-lg text-xs",
  lg: "size-14 rounded-xl text-lg",
  xl: "size-20 rounded-2xl text-2xl",
} as const;

type AvatarSize = keyof typeof sizeStyles;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

function toneFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return avatarTones[hash % avatarTones.length] ?? avatarTones[0];
}

interface PersonAvatarProps {
  name: string;
  /** Seed for the deterministic tint — usually the person's id. Defaults to name. */
  seed?: string;
  size?: AvatarSize;
  className?: string;
  /** Profile-picture URL. Falls back to initials when absent or it fails to load. */
  imageUrl?: string | null;
}

export function PersonAvatar({ name, seed, size = "md", className, imageUrl }: PersonAvatarProps) {
  // Track the URL that failed (not a bare boolean) so a later swap to a new
  // picture re-attempts the image instead of staying stuck on initials.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = Boolean(imageUrl) && imageUrl !== failedUrl;

  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden font-semibold font-display",
        sizeStyles[size],
        // The tint only shows behind the initials fallback.
        showImage ? "bg-muted" : toneFor(seed ?? name),
        className,
      )}
    >
      {showImage ? (
        <img
          src={imageUrl ?? undefined}
          alt=""
          className="size-full object-cover"
          onError={() => setFailedUrl(imageUrl ?? null)}
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}
